// ---------------------------------------------------------------------------
// OAuth Provider Definitions — token URLs, scopes, and client config
// per integration that supports OAuth2 authorization code flow.
// ---------------------------------------------------------------------------

export interface OAuthProviderConfig {
  /** Integration definition ID */
  definitionId: string;
  /** OAuth2 authorization endpoint */
  authorizeUrl: string;
  /** OAuth2 token endpoint */
  tokenUrl: string;
  /** Optional token revocation endpoint */
  revokeUrl?: string;
  /**
   * Optional grant-revocation endpoint used to FORCE a fresh consent screen on
   * reconnect. Distinct from `revokeUrl` (which revokes a single token): this
   * revokes the user's entire authorization *grant* for the OAuth app, so the
   * next /authorize redirect cannot be silently short-circuited.
   *
   * GitHub is the motivating case (ENG-6187). GitHub does NOT honour
   * `prompt=consent` — once a user has authorized an OAuth App, the authorize
   * endpoint redirects straight back carrying the *previously granted* (and
   * possibly narrower) scope set. A reconnect therefore can never widen scopes:
   * the user keeps landing on the same missing-scopes banner in a loop. Revoking
   * the grant before redirecting forces GitHub to re-display consent so the new
   * scopes are actually granted.
   *
   * `{client_id}` is substituted with the resolved client id at call time. The
   * call requires HTTP Basic auth (client_id:client_secret) and the user's
   * access_token in the request body. It is best-effort — a failed revoke must
   * never block the reconnect (the worst case is the pre-fix behaviour).
   */
  grantRevokeUrl?: string;
  /** Default scopes to request */
  defaultScopes: string[];
  /**
   * Optional read-only scope set. When the /authorize caller passes
   * `read_only: true`, these scopes are requested instead of `defaultScopes`
   * (and the ENG-4956 agent-required-scope union is skipped — read-only is an
   * explicit operator choice that deliberately declines write access). Providers
   * without this field reject a read_only request. The granted token's actual
   * scopes (`credentials.granted_scopes`) remain the source of truth for whether
   * an install is read-only — this list only governs what consent is requested.
   */
  readOnlyScopes?: string[];
  /** Whether the provider supports refresh tokens */
  supportsRefresh: boolean;
  /** Additional params to include in the authorize URL */
  extraAuthorizeParams?: Record<string, string>;
  /** How to send client credentials in token exchange ('body' or 'basic') */
  clientAuthMethod: 'body' | 'basic';
  /** Provider-specific function to extract user info from tokens for status_message */
  userInfoUrl?: string;
  /**
   * PKCE method. Set to 'S256' for providers that mandate (or recommend) PKCE.
   * When set, the shared /authorize route generates a code_verifier, stores it
   * with the OAuth state row, and sends code_challenge + code_challenge_method
   * on the authorize URL. The /callback route retrieves the verifier from
   * state and includes it in the token exchange. Public clients (token_endpoint_auth_method: none)
   * with PKCE skip the client_secret on the token exchange.
   */
  pkce?: 'S256';
  /**
   * Whether the OAuth client can authenticate without a client_secret (RFC 6749
   * "public client", typically combined with PKCE). When true, the token
   * exchange POST omits client_secret and only sends client_id. Defaults to
   * false (confidential client; client_secret required).
   */
  publicClient?: boolean;
  /**
   * Remote streamable-HTTP MCP endpoint hosted by the provider. When set, the
   * Claude Code provisioner emits a `.mcp.json` entry pointing at this URL
   * with an `Authorization: Bearer ${ACCESS_TOKEN}` header sourced from the
   * integration's credentials. Lets new remote-MCP integrations ride the
   * shared OAuth registry + refresh path instead of carrying hand-rolled
   * blocks in `buildMcpJson`.
   */
  mcpUrl?: string;
  /**
   * Curated allowlist of tool names this remote MCP should expose to the agent
   * (ENG-6948). Remote MCP servers can advertise a far larger surface than the
   * catalog curates (Kajabi advertises 111 tools; the catalog curates 25), and
   * a direct/proxied remote MCP is otherwise all-or-nothing. When set, the
   * remote-oauth-proxy filters `tools/list` to these names and rejects
   * `tools/call` for anything outside the set (see AGT_REMOTE_MCP_TOOL_ALLOWLIST
   * in `remote-oauth-proxy.ts`). Must stay in sync with the catalog seed's
   * `defined_scopes[].tools` for this definition - the drift-guard test in
   * `__tests__/oauth-provider-tool-allowlist.test.ts` enforces that. Unset =
   * no filtering (full pass-through), the default for every other provider.
   */
  toolAllowlist?: readonly string[];
}

export const OAUTH_PROVIDERS: Record<string, OAuthProviderConfig> = {
  'google-workspace': {
    definitionId: 'google-workspace',
    authorizeUrl: 'https://accounts.google.com/o/oauth2/v2/auth',
    tokenUrl: 'https://oauth2.googleapis.com/token',
    revokeUrl: 'https://oauth2.googleapis.com/revoke',
    defaultScopes: [
      'https://www.googleapis.com/auth/gmail.modify',
      'https://www.googleapis.com/auth/calendar',
      'https://www.googleapis.com/auth/drive',
      'https://www.googleapis.com/auth/spreadsheets',
      'https://www.googleapis.com/auth/documents',
      'https://www.googleapis.com/auth/chat.messages',
      'https://www.googleapis.com/auth/chat.spaces.readonly',
    ],
    supportsRefresh: true,
    extraAuthorizeParams: {
      access_type: 'offline',
      prompt: 'consent',
    },
    clientAuthMethod: 'body',
    userInfoUrl: 'https://www.googleapis.com/oauth2/v2/userinfo',
  },

  'github': {
    definitionId: 'github',
    authorizeUrl: 'https://github.com/login/oauth/authorize',
    tokenUrl: 'https://github.com/login/oauth/access_token',
    // ENG-6187: revoke the existing grant on reconnect so GitHub re-prompts and
    // the four scopes below are actually granted. Without this, a stale narrow
    // grant (e.g. an old read:user-only authorization) is silently re-issued and
    // the missing-scopes banner loops forever.
    grantRevokeUrl: 'https://api.github.com/applications/{client_id}/grant',
    defaultScopes: ['repo', 'read:org', 'gist', 'workflow'],
    supportsRefresh: true,
    extraAuthorizeParams: {},
    clientAuthMethod: 'body',
    userInfoUrl: 'https://api.github.com/user',
  },

  'granola': {
    // Granola MCP — remote streamable-HTTP at https://mcp.granola.ai/mcp.
    // The AS is at mcp-auth.granola.ai and exposes RFC 8414 metadata at
    // /.well-known/oauth-authorization-server. Auth is OAuth 2.0 with
    // mandatory PKCE (S256) and a public client (no client_secret) issued
    // via Dynamic Client Registration (RFC 7591). The bootstrap script
    // (`packages/api/scripts/dcr-register.ts`) registers a client once at
    // deploy time; OAUTH_GRANOLA_CLIENT_ID is set from its output.
    definitionId: 'granola',
    authorizeUrl: 'https://mcp-auth.granola.ai/oauth2/authorize',
    tokenUrl: 'https://mcp-auth.granola.ai/oauth2/token',
    // Minimal scope set: `offline_access` earns the refresh_token so the
    // refresh cron can rotate the bearer without operator action; `openid`
    // is required for the OIDC code flow even when we don't request an
    // id_token. Profile/email are intentionally omitted — we have no
    // userInfoUrl wired up here, so requesting them would over-ask consent
    // for fields the callback can't read.
    defaultScopes: ['openid', 'offline_access'],
    supportsRefresh: true,
    extraAuthorizeParams: {},
    clientAuthMethod: 'body',
    pkce: 'S256',
    publicClient: true,
    mcpUrl: 'https://mcp.granola.ai/mcp',
    // Curated surface (matches the catalog seed's defined_scopes[].tools).
    toolAllowlist: ['search-meetings', 'read-transcript', 'read-summary', 'list-folders'],
  },

  'brand-ninja': {
    // ENG-6820: Brand Ninja External-Content MCP, remote streamable-HTTP at
    // https://ext-api.app.brandninja.ai/v1/mcp. Same shape as Granola: the
    // server exposes RFC 8414 authorization-server metadata at
    // /.well-known/oauth-authorization-server (values below are taken verbatim
    // from that document, not inferred). Auth is OAuth 2.0 authorization-code
    // with mandatory PKCE (S256) and a public client (token_endpoint_auth_method
    // 'none') issued via Dynamic Client Registration (RFC 7591). The bootstrap
    // script (packages/api/scripts/dcr-register.ts) registers a client once at
    // deploy time against the registration_endpoint
    // (https://ext-api.app.brandninja.ai/v1/oauth/register); OAUTH_BRAND_NINJA_CLIENT_ID
    // is set from its output. The AS advertises the refresh_token grant, so the
    // shared oauth-refresh cron rotates the bearer without operator action.
    definitionId: 'brand-ninja',
    authorizeUrl: 'https://prod-brandninja.auth.ap-southeast-2.amazoncognito.com/oauth2/authorize',
    tokenUrl: 'https://prod-brandninja.auth.ap-southeast-2.amazoncognito.com/oauth2/token',
    // The resource server (ext-api.app.brandninja.ai) advertises exactly two
    // scopes: external-api/content.write (the default content surface) and
    // external-api/admin (read-only credential metadata, granted per account
    // admin). Default install is least-privilege: content.write only; an
    // operator can widen to admin out of band. No openid/offline_access in the
    // advertised scope set, so Cognito issues the refresh_token for the code grant
    // regardless, so requesting only the resource scope keeps consent minimal.
    defaultScopes: ['external-api/content.write'],
    supportsRefresh: true,
    extraAuthorizeParams: {},
    clientAuthMethod: 'body',
    pkce: 'S256',
    publicClient: true,
    mcpUrl: 'https://ext-api.app.brandninja.ai/v1/mcp',
    // Curated surface (matches the catalog seed's defined_scopes[].tools).
    toolAllowlist: ['submit_content_request', 'get_content_status', 'list_content_requests', 'list_channels', 'list_credentials'],
  },

  'kajabi': {
    // Kajabi MCP — remote streamable-HTTP at https://mcp.kajabi.com/mcp.
    // Same Granola/Brand-Ninja shape: OAuth 2.0 authorization-code with
    // mandatory PKCE (S256) and a public client (token_endpoint_auth_method
    // 'none') issued via Dynamic Client Registration (RFC 7591). Values below
    // are taken verbatim from Kajabi's RFC 8414 metadata at
    // https://mcp.kajabi.com/.well-known/oauth-authorization-server (a Rails
    // Doorkeeper AS), not inferred. NOTE the authorize host differs from the
    // token host: authorize is on app.kajabi.com (the login surface), while
    // token/register/revoke are on mcp.kajabi.com — do NOT "normalise" them to
    // one host. The bootstrap script (packages/api/scripts/dcr-register.ts)
    // registers a client once at deploy time against
    // https://mcp.kajabi.com/mcp/oauth/register; OAUTH_KAJABI_CLIENT_ID is set
    // from its output. Doorkeeper RESTRICTS a dynamic client to the scopes it
    // registered with, so register with the union of defaultScopes below
    // (--scope 'read write:contacts write:emails'). The AS advertises the
    // refresh_token grant (no openid/offline_access scope needed), so the
    // shared oauth-refresh cron rotates the bearer without operator action.
    definitionId: 'kajabi',
    authorizeUrl: 'https://app.kajabi.com/mcp/oauth/authorize',
    tokenUrl: 'https://mcp.kajabi.com/mcp/oauth/token',
    revokeUrl: 'https://mcp.kajabi.com/mcp/oauth/revoke',
    // Coarse Doorkeeper scopes (NOT openid-style). Least-privilege v1: cross-
    // domain reads (`read`) plus the two write surfaces this integration ships
    // (contact tags/segments, email broadcasts/sequences). Widen here (and
    // re-register the DCR client) when adding commerce/content/publish scopes.
    defaultScopes: ['read', 'write:contacts', 'write:emails'],
    supportsRefresh: true,
    extraAuthorizeParams: {},
    clientAuthMethod: 'body',
    pkce: 'S256',
    publicClient: true,
    mcpUrl: 'https://mcp.kajabi.com/mcp',
    // Curated surface (matches the catalog seed's defined_scopes[].tools). Kajabi's
    // live MCP advertises ~111 tools; the agent only needs these 25.
    toolAllowlist: [
      'list_sites', 'get_site_summary', 'select_site', 'search_contacts', 'get_contact',
      'search_products', 'get_product', 'list_offers', 'get_offer', 'list_offer_purchases',
      'get_revenue_analytics', 'get_contacts_analytics', 'list_tags', 'create_tag', 'tag_contact',
      'untag_contact', 'list_segments', 'create_segment', 'update_segment', 'list_broadcasts',
      'get_broadcast', 'create_broadcast', 'list_sequences', 'get_sequence', 'create_sequence',
    ],
  },

  'notion-cli': {
    // Notion's public OAuth app. Tokens are workspace-scoped and long-lived —
    // Notion does not issue refresh_tokens, so `supportsRefresh: false` and
    // the refresh cron skips this provider entirely. Scopes are not part of
    // Notion's authorize URL contract; consent is governed by what the user
    // grants in the OAuth screen, so `defaultScopes` stays empty.
    // `owner=user` forces the user-OAuth variant (vs internal integration).
    // Requires OAUTH_NOTION_CLI_CLIENT_ID and OAUTH_NOTION_CLI_CLIENT_SECRET.
    definitionId: 'notion-cli',
    authorizeUrl: 'https://api.notion.com/v1/oauth/authorize',
    tokenUrl: 'https://api.notion.com/v1/oauth/token',
    defaultScopes: [],
    supportsRefresh: false,
    extraAuthorizeParams: {
      owner: 'user',
    },
    clientAuthMethod: 'basic',
  },

  'xero': {
    definitionId: 'xero',
    authorizeUrl: 'https://login.xero.com/identity/connect/authorize',
    tokenUrl: 'https://identity.xero.com/connect/token',
    revokeUrl: 'https://identity.xero.com/connect/revocation',
    defaultScopes: [
      'openid',
      'profile',
      'email',
      'offline_access',
      // Granular scopes (required for apps created after March 2, 2026 —
      // do NOT revert to the broad `accounting.transactions` /
      // `accounting.contacts` scopes, Xero rejects the manifest).
      // The variant *without* `.read` is the read+write granular scope.
      'accounting.settings.read',
      // contacts: write enables agent-driven supplier/customer creation
      // (required for bill creation since a bill must reference a contact).
      'accounting.contacts',
      // invoices: write enables bill creation (Type=ACCPAY invoices) and
      // updates to sales invoices alongside the existing read access.
      'accounting.invoices',
      // attachments: write enables agents to attach the source PDF to a
      // bill at creation time. Read-only would force a follow-up manual
      // upload in Xero; write closes the loop.
      'accounting.attachments',
      // accounting.transactions.read → granular read-only replacements
      // for the surfaces we don't yet need write access on.
      'accounting.payments.read',
      'accounting.banktransactions.read',
      'accounting.manualjournals.read',
      // accounting.reports.read → granular read-only replacements
      'accounting.reports.balancesheet.read',
      'accounting.reports.profitandloss.read',
      'accounting.reports.trialbalance.read',
      'accounting.reports.budgetsummary.read',
      'accounting.reports.banksummary.read',
      'accounting.reports.executivesummary.read',
      'accounting.reports.aged.read',
    ],
    // Read-only variant (ENG-6170): the `.read` granular scope for every
    // surface, so a token granted under it cannot create bills/invoices or
    // mutate contacts/attachments. Used when an install is (re)connected with
    // `read_only: true` — e.g. to lock a production-books integration to reads
    // until per-vendor broker mediation (ENG-4922) gates its writes.
    readOnlyScopes: [
      'openid',
      'profile',
      'email',
      'offline_access',
      'accounting.settings.read',
      'accounting.contacts.read',
      'accounting.invoices.read',
      'accounting.attachments.read',
      'accounting.payments.read',
      'accounting.banktransactions.read',
      'accounting.manualjournals.read',
      'accounting.reports.balancesheet.read',
      'accounting.reports.profitandloss.read',
      'accounting.reports.trialbalance.read',
      'accounting.reports.budgetsummary.read',
      'accounting.reports.banksummary.read',
      'accounting.reports.executivesummary.read',
      'accounting.reports.aged.read',
    ],
    supportsRefresh: true,
    extraAuthorizeParams: {},
    clientAuthMethod: 'basic',
    userInfoUrl: 'https://api.xero.com/connections',
  },
};

/**
 * Split a stored OAuth `granted_scopes` value into individual scope tokens.
 *
 * OAuth servers disagree on the delimiter. RFC 6749 mandates a single
 * space-separated string (Xero, Google, Granola all comply), but **GitHub
 * returns its granted scopes COMMA-separated** — e.g. `repo,read:org,gist,workflow`
 * — in both the token-response `scope` field and the `X-OAuth-Scopes` header.
 * We persist that value verbatim (`credentials.granted_scopes = tokenData.scope`
 * in the OAuth callback), so any consumer that set-diffs the stored value against
 * a provider's `defaultScopes` MUST tolerate both delimiters. A whitespace-only
 * split collapses GitHub's whole comma-joined string into ONE unmatchable token,
 * making every default scope look missing — the bug behind the permanent amber
 * "requires reconnecting" wrench on every GitHub-bound agent, persisting even
 * immediately after a fresh, fully-scoped reconnect (ENG-6237).
 *
 * No OAuth scope token legitimately contains a space or a comma, so splitting on
 * either is safe for every provider. Array elements are split too, in case the
 * storage layer ever proxies a delimited string inside a single-element array.
 */
export function parseGrantedScopes(grantedRaw: unknown): string[] {
  if (typeof grantedRaw === 'string') {
    return grantedRaw.split(/[\s,]+/).filter(Boolean);
  }
  if (Array.isArray(grantedRaw)) {
    return grantedRaw
      .filter((s): s is string => typeof s === 'string')
      .flatMap((s) => s.split(/[\s,]+/))
      .filter(Boolean);
  }
  return [];
}

export function getOAuthProvider(definitionId: string): OAuthProviderConfig | undefined {
  return OAUTH_PROVIDERS[definitionId];
}

export function isOAuthIntegration(definitionId: string): boolean {
  return definitionId in OAUTH_PROVIDERS;
}

/**
 * Resolve the BASE consent scopes for a provider, honouring an explicit
 * read-only request (ENG-6170).
 *
 * - Default: returns `provider.defaultScopes`.
 * - `readOnly: true`: returns `provider.readOnlyScopes`, or a fail-loud error
 *   when the provider has no read-only set configured (never silently fall back
 *   to the write scopes — that would defeat the point of asking for read-only).
 *
 * This deliberately does NOT apply the agent-required-scope union (ENG-4956);
 * that is the /authorize route's responsibility and is intentionally skipped
 * for read-only requests.
 */
export function resolveBaseConsentScopes(
  provider: OAuthProviderConfig,
  opts: { readOnly?: boolean } = {},
): { ok: true; scopes: string[] } | { ok: false; error: string } {
  if (opts.readOnly) {
    if (!provider.readOnlyScopes || provider.readOnlyScopes.length === 0) {
      return {
        ok: false,
        error: `read_only is not supported for '${provider.definitionId}' (no read-only scope set configured)`,
      };
    }
    return { ok: true, scopes: [...provider.readOnlyScopes] };
  }
  return { ok: true, scopes: [...provider.defaultScopes] };
}
